import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stripHTML } from '../../utils/ids-xss-utils/ids-xss-utils';
import { contrastColor, adjustColor } from '../../utils/ids-color-utils/ids-color-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsKeyboardMixin from '../../mixins/ids-keyboard-mixin/ids-keyboard-mixin';
import IdsElement from '../../core/ids-element';
import styles from './ids-header.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

const Base = IdsKeyboardMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Header Component
 * @type {IdsHeader}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsKeyboardMixin
 */
@customElement('ids-header')
@scss(styles)
export default class IdsHeader extends Base {
  colorVariants: string[] = ['alternate'];

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.#initHeaderTab();
  }

  static get attributes() {
    return [
      ...super.attributes,
      attributes.COLOR,
      attributes.COMPACT,
      attributes.STICKY,
      attributes.STICKY_TOP
    ];
  }

  /**
   * Create the template for the header contents
   * @returns {string} The template
   */
  template(): string {
    return `
    <div class="ids-header">
      <slot></slot>
    </div>`;
  }

  /**
   * Sets the color attribute
   * @param {string} value string value for color
   */
  set color(value: string) {
    if (typeof value !== 'string' || !value.length || value === '' || value === '#ffffff') {
      this.container?.style.removeProperty('--ids-header-color-background');
      this.container?.style.removeProperty('--ids-header-color-border-bottom');
      this.container?.style.removeProperty('--ids-header-color-text');
      this.container?.style.removeProperty('--ids-header-button-color-text-default');
      this.container?.style.removeProperty('--ids-header-button-color-background-hover');
      this.container?.style.removeProperty('--ids-header-button-color-border-hover');
      this.container?.style.removeProperty('--ids-header-button-color-text-hover');
      this.container?.style.removeProperty('--ids-header-input-color-border-hover');
      this.container?.style.removeProperty('--ids-header-button-color-text-disabled');
      this.container?.style.removeProperty('--ids-header-button-shadow-focus');
      this.container?.style.removeProperty('--ids-header-button-color-border-focus');
      this.container?.style.removeProperty('--ids-header-button-color-background-pressed');
      this.container?.style.removeProperty('--ids-header-button-color-border-pressed');
      this.container?.style.removeProperty('--ids-header-button-color-text-pressed');
      this.container?.style.removeProperty('--ids-header-button-menu-color-background-active');
      this.container?.style.removeProperty('--ids-header-button-menu-color-active');
      this.container?.style.removeProperty('--ids-button-tertiary-shadow-focus');

      // Breadcrumbs
      this.container?.style.removeProperty('--ids-breadcrumb-color-text-default');
      this.container?.style.removeProperty('--ids-breadcrumb-color-text-selected');
      this.container?.style.removeProperty('--ids-breadcrumb-color-text-disabled');
      this.container?.style.removeProperty('--ids-breadcrumb-color-text-hover');
      this.container?.style.removeProperty('--ids-header-breadcrumb-gradient');

      this.removeAttribute('color');
      return;
    }
    const sanitzedVal = stripHTML(value);
    const textColor = contrastColor(sanitzedVal, '#ffffff', '#000000');

    this.container?.style.setProperty('--ids-header-color-background', sanitzedVal);
    this.container?.style.setProperty('--ids-header-color-border-bottom', adjustColor(sanitzedVal, -0.30));
    this.container?.style.setProperty('--ids-header-color-text', textColor);
    this.container?.style.setProperty('--ids-header-button-color-text-default', textColor);
    this.container?.style.setProperty('--ids-header-button-color-background-hover', textColor === '#ffffff' ? 'rgba(0 0 0 / 0.2)' : 'rgba(255 255 255 / 0.1)');
    this.container?.style.setProperty('--ids-header-button-color-border-hover', 'transparent');
    this.container?.style.setProperty('--ids-header-button-color-text-hover', textColor);
    this.container?.style.setProperty('--ids-header-input-color-border-hover', textColor);
    this.container?.style.setProperty('--ids-header-button-color-text-disabled', adjustColor(textColor, -0.20));
    this.container?.style.setProperty('--ids-header-button-shadow-focus', `0 0 0 2px ${sanitzedVal}, 0 0 0 3px ${textColor}`);
    this.container?.style.setProperty('--ids-header-button-color-border-focus', textColor);
    this.container?.style.setProperty('--ids-header-button-color-background-pressed', textColor === '#ffffff' ? 'rgba(0 0 0 / 0.2)' : 'rgba(255 255 255 / 0.1)');
    this.container?.style.setProperty('--ids-header-button-color-border-pressed', 'transparent');
    this.container?.style.setProperty('--ids-header-button-color-text-pressed', textColor);
    this.container?.style.setProperty('--ids-header-button-menu-color-background-active', textColor === '#ffffff' ? 'rgba(0 0 0 / 0.2)' : 'rgba(255 255 255 / 0.1)');
    this.container?.style.setProperty('--ids-header-button-menu-color-active', textColor);
    this.container?.style.setProperty('--ids-button-tertiary-shadow-focus', textColor);

    // Breadcrumbs
    this.container?.style.setProperty('--ids-breadcrumb-color-text-default', textColor);
    this.container?.style.setProperty('--ids-breadcrumb-color-text-selected', textColor);
    this.container?.style.setProperty('--ids-breadcrumb-color-text-disabled', textColor === '#ffffff' ? 'rgba(255 255 255 / 0.4)' : 'rgba(0 0 0 / 0.4)');
    this.container?.style.setProperty('--ids-breadcrumb-color-text-hover', textColor);
    this.container?.style.setProperty('--ids-header-breadcrumb-gradient', `linear-gradient(to right, ${sanitzedVal}, ${textColor === '#ffffff' ? 'rgba(255 255 255 / 0)' : 'rgba(0 0 0 / 0)'})`);

    this.setAttribute('color', sanitzedVal);
  }

  get color(): string {
    return this.getAttribute('color') || '#ffffff';
  }

  /**
   * Sets the compact attribute
   * @param {boolean} value true if the header should be compact
   */
  set compact(value: boolean) {
    const isCompact = stringToBool(value);

    if (isCompact) {
      this.setAttribute(attributes.COMPACT, '');
      this.container?.classList.add('compact');
    } else {
      this.removeAttribute(attributes.COMPACT);
      this.container?.classList.remove('compact');
    }
  }

  /**
   * Gets the compact attribute
   * @returns {boolean} true if the header is compact
   */
  get compact(): boolean {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }

  /**
   * Sets the sticky attribute
   * @param {boolean} value true if the header should be sticky
   */
  set sticky(value: boolean) {
    const isSticky = stringToBool(value);

    if (isSticky) {
      this.setAttribute(attributes.STICKY, '');
      this.container?.classList.add('sticky');
    } else {
      this.removeAttribute(attributes.STICKY);
      this.container?.classList.remove('sticky');
    }
  }

  /**
   * Gets the sticky attribute
   * @returns {boolean} true if the header is sticky
   */
  get sticky(): boolean {
    return stringToBool(this.getAttribute(attributes.STICKY));
  }

  /**
   * Sets the sticky-top attribute
   * @param {string} value CSS value for top position when sticky
   */
  set stickyTop(value: string) {
    if (value) {
      this.setAttribute(attributes.STICKY_TOP, value);
      this.container?.style.setProperty('--ids-header-sticky-top', value);
    } else {
      this.removeAttribute(attributes.STICKY_TOP);
      this.container?.style.removeProperty('--ids-header-sticky-top');
    }
  }

  /**
   * Gets the sticky-top attribute
   * @returns {string} CSS value for top position when sticky
   */
  get stickyTop(): string {
    return this.getAttribute(attributes.STICKY_TOP) || '0';
  }

  /**
   * Gets the elements assigned to the header slot
   * @returns {Element[] | []} Array of slotted elements or empty array
   */
  get slotElements(): Element[] | [] {
    return (this.shadowRoot?.querySelector('.ids-header slot') as HTMLSlotElement)?.assignedElements() || [];
  }

  /**
   * Initializes header tab styling by checking for tab groups
   * @private
   */
  #initHeaderTab() {
    const headerTabGroup = this.slotElements.find((element) => (element.nodeName.toLowerCase() === 'ids-tab-group'));

    if (headerTabGroup) {
      this.shadowRoot?.querySelector('.ids-header')?.classList.add('no-bottom-border');
    }
  }
}
